import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as MediaAssetAPI from '@/api/media-asset';
import { useCurrentApp } from '@/stores/userDataStore';
import { ImageProviders, MediaAsset, MediaAssetType } from '@/types/media';
import { uploadTempFileToS3, UserUploadedFile } from '@/api/upload-temp-file';
import { ImagesModalSectionProps } from '@/features/media-modal/components/common/commonTypes';
import { getOutputBucketUrl } from '@/utils/public-urls';
import { sanitizeFileName } from '@/api/s3';
export type UploadMediaAssetModalSectionProps = ImagesModalSectionProps;

const VALID_VIDEO_TYPES = ['video/mp4', 'video/quicktime'];
const VALID_IMAGE_TYPES = ['image/webp', 'image/jpeg', 'image/png'];
const VALID_TYPES = [...VALID_IMAGE_TYPES, ...VALID_VIDEO_TYPES];

type UploadFile = {
  id?: string;
  uploadedTempFile?: UserUploadedFile;
  progress: number;
  isUploaded: boolean;
  type: string;
  size: number;
  file: File;
};

export const useUploadMediaAsset = ({
  setSelectedMedia,
  multiSelection = true,
}: {
  setSelectedMedia: (media: MediaAsset[] | ((prev: MediaAsset[]) => MediaAsset[])) => void;
  multiSelection?: boolean;
}) => {
  const { appKey, appUrl } = useCurrentApp();
  const [files, setFiles] = useState<UploadFile[]>([]);
  const queryClient = useQueryClient();

  const createMediaAssetMutation = useMutation({
    mutationFn: async (uploadedFile: UserUploadedFile) => {
      return MediaAssetAPI.uploadMediaAsset({
        appKey,
        appUrl,
        file: uploadedFile,
      });
    },
    onSuccess: (data, uploadedFile) => {
      const uploadingFile = files.find((f) => f.uploadedTempFile?.s3Key === uploadedFile.s3Key);
      const isVideo = VALID_VIDEO_TYPES.includes(uploadingFile?.type || '');

      const assetWithProperSrc = {
        ...data.mediaAsset,
        src: data.mediaAsset.result ? `${getOutputBucketUrl()}/${data.mediaAsset.result}` : data.mediaAsset.src,
      };

      const newMediaAsset = {
        id: data.mediaAsset.id,
        name: data.mediaAsset.name,
        alt: data.mediaAsset.name,
        provider: ImageProviders.uploaded,
        type: isVideo ? MediaAssetType.video : MediaAssetType.image,
        src: assetWithProperSrc.src,
        status: data.mediaAsset.status,
      } satisfies MediaAsset;

      queryClient.setQueryData(['uploaded-assets', appKey], (old: any) => ({
        mediaAssets: [newMediaAsset, ...(old?.mediaAssets || [])],
      }));

      setSelectedMedia((prev: MediaAsset[]) => {
        if (!multiSelection) {
          return [assetWithProperSrc as MediaAsset];
        }
        return [...prev, assetWithProperSrc as MediaAsset];
      });
      setFiles((prev) => prev.filter((f) => f.uploadedTempFile?.s3Key !== uploadedFile.s3Key));
    },
  });

  const handleUpload = async (filesToUpload: File[]) => {
    const validFiles = filesToUpload
      .filter((file) => VALID_TYPES.includes(file.type))
      .map((file) => {
        const sanitizedName = sanitizeFileName(file.name);
        return new File([file], sanitizedName, { type: file.type });
      });

    if (!validFiles.length) return;

    const newFiles = validFiles.map((file) => ({
      type: file.type,
      size: file.size,
      file,
      progress: 0,
      isUploaded: false,
    }));

    setFiles((prev) => [...prev, ...newFiles]);

    validFiles.forEach(async (file) => {
      const uploadedTempFile = await uploadTempFileToS3({
        appKey,
        fileName: file.name,
        file,
        progressCallback: (progress) => {
          setFiles((prev) => prev.map((f) => (f.file === file ? { ...f, progress } : f)));
        },
      });

      setFiles((prev) => prev.map((f) => (f.file === file ? { ...f, uploadedTempFile } : f)));

      createMediaAssetMutation.mutate(uploadedTempFile);
    });
  };

  return {
    files,
    handleUpload,
  };
};
